/** Payout status lifecycle */
export type PayoutStatus = 'calculated' | 'transferred' | 'failed';

/** Stripe Connect onboarding status */
export type StripeConnectStatus = 'pending' | 'active' | 'restricted' | null;

/** Public creator info shown on profile */
export interface PublicCreatorInfo {
  isCreator: boolean;
  resolvedPicks: number;
  followerCount: number;
}

/** Reason a follower is not eligible for payout */
export type IneligibilityReason = 'follow_too_recent' | 'no_sportsbook' | 'insufficient_wagering';

/** Creator eligibility and payout constants */
export const CREATOR_ELIGIBILITY = {
  /** Minimum resolved picks before a user's profile is followable */
  MIN_RESOLVED_PICKS: 50,
  /** Monthly tailing fee per creator in cents ($4.99) */
  FOLLOW_PRICE_CENTS: 499,
  /** Platform/creator split percentage (50/50) */
  PLATFORM_SPLIT_PCT: 50,
  CREATOR_SPLIT_PCT: 50,
  /** Minimum days following before eligible */
  MIN_FOLLOW_DAYS: 30,
  /** Minimum wagered per billing period in cents ($100) */
  MIN_WAGERED_CENTS: 10_000,
  /** Maximum days since last settled bet before a creator is considered inactive */
  MAX_INACTIVE_DAYS: 60,
  /** Stripe fee estimate for transfers */
  STRIPE_FEE_PCT: 2.9,
  STRIPE_FEE_FIXED_CENTS: 30,
} as const;

/** Summary of a single payout period for a creator */
export interface CreatorPayoutSummary {
  id: string;
  periodLabel: string;
  totalFollowers: number;
  eligibleFollowers: number;
  grossRevenueCents: number;
  stripeFeesCents: number;
  platformShareCents: number;
  creatorShareCents: number;
  status: PayoutStatus;
  calculatedAt: string;
  transferredAt: string | null;
}

/** Follower eligibility detail for the creator dashboard */
export interface FollowerEligibilityDetail {
  followerId: string;
  displayName: string;
  followedAt: string;
  followDurationDays: number;
  hasSportsbookLinked: boolean;
  wageredCents: number;
  isEligible: boolean;
  ineligibilityReason: IneligibilityReason | null;
}

/** Creator status response */
export interface CreatorStatus {
  isCreator: boolean;
  resolvedPicks: number;
  followerCount: number;
  hasStripeConnect: boolean;
  stripeConnectStatus: StripeConnectStatus;
  totalEarnedCents: number;
}

// =============================================================================
// Display Constants (shared across web + mobile UI)
// =============================================================================

import { formatCents } from './formatting';

/** Human-readable labels for ineligibility reasons */
export const INELIGIBILITY_LABELS: Record<IneligibilityReason, string> = {
  follow_too_recent: 'Too Recent',
  no_sportsbook: 'No Sportsbook',
  insufficient_wagering: `Under ${formatCents(CREATOR_ELIGIBILITY.MIN_WAGERED_CENTS)}`,
};

/** Human-readable labels for payout statuses */
export const PAYOUT_STATUS_LABELS: Record<PayoutStatus, string> = {
  calculated: 'Pending',
  transferred: 'Paid',
  failed: 'Failed',
};

/** Result from running the monthly payout calculation */
export interface PayoutCalculationResult {
  periodLabel: string;
  creatorsProcessed: number;
  payoutsCreated: number;
  totalEligibleFollowers: number;
  totalGrossRevenueCents: number;
  errors: Array<{ creatorId: string; error: string }>;
}
